import { Atom, Getter, atom } from 'jotai';
import { omitBy, isEmpty, flatten } from 'lodash';
import { isNumberOptionArray, NumberOption } from '@/types/generic';
import { toObject } from '@/utils/object';
import { isNullish } from '@/utils/predicates';
import type { FilterOperator, FilterParams, ObjectValue } from '@statsbomb/parachute-types';

const emptyFilterParams: FilterParams = {
  gte: {},
  lte: {},
  in: {},
  inside: {},
  null: {},
} as const;

export type FilterParamsObject = Record<string, ObjectValue>;

/**
 * filter params functions have an optional piece of metadata on the function to say whether the filter should be
 * hidden from the user.
 */
type FilterParamsFn = {
  (get: Getter): FilterParamsObject;
  isHidden?: boolean;
};

/** Returns a function that generates a filter params object by using the key name and value of the given atom. */
export const objectFilterParamsFn =
  (atom: Atom<ObjectValue | NumberOption[]>, key: string): FilterParamsFn =>
  (get: Getter): FilterParamsObject => {
    const value = get(atom);
    // If the value is an array of options, we need to extract the values from the options and use that as the value in
    // the filter params object
    if (isNumberOptionArray(value)) {
      return toObject(
        key,
        value.map(({ value }) => value),
      );
    }
    return toObject(key, value);
  };

/** Returns a function that retrieves a filter parameters object from a specified key within a given atom. */
export const propertyFilterParamsFn =
  <K extends string>(atom: Atom<Record<K, FilterParamsObject>>, key: K): FilterParamsFn =>
  (get: Getter): FilterParamsObject =>
    get(atom)[key];

/** Returns a function that retrieves a filter parameters object stored in the given atom. */
export const plainFilterParamsFn =
  (atom: Atom<FilterParamsObject>): FilterParamsFn =>
  (get: Getter): FilterParamsObject =>
    get(atom);

/** Returns a function that retrieves a filter parameters object stored in the given atom. */
export const constantFilterParamsFn = (key: string, value: ObjectValue): FilterParamsFn => {
  const filterParamsFn: FilterParamsFn = (_get: Getter): FilterParamsObject => ({ [key]: value });
  filterParamsFn.isHidden = true;
  return filterParamsFn;
};

/**
 * Filter definitions contain all the information needed to derive filter objects (that are, in turn, used to derive
 * API query strings). Filter definitions return a filter object in the format `{filter_key: 'filter value'}`.
 */
type SingleFilterDefinition = {
  /**
   * A function that will return the current filter params for this definition. The filter params returned by this
   * function should be used when querying the API. If no {@link visibleFilterParams} are supplied, the filter params
   * should also be used to calculate the number of active filters.
   */
  apiFilterParams: FilterParamsFn;

  /**
   * A function that will return the current visible filter params for this definition. This is occasionally necessary
   * when we don't want to show the user the _actually_ selected filter value, for example, if a user selects no
   * competitions we want to show an empty selection, however the API will actually be sent _every_ competition. This
   * function should be used to calculate the number of active filters if it is provided.
   */
  visibleFilterParams?: FilterParamsFn;

  /** How is this value compared when filtering in the DB? */
  filterOperator: FilterOperator;
};

export type FilterDefinition = SingleFilterDefinition | SingleFilterDefinition[];

const isVisibleFilter = (filterDefinition: SingleFilterDefinition) => !filterDefinition.apiFilterParams.isHidden;

const hasAtLeastOneActiveFilterInGroup = (get: Getter, filterDefinitions: SingleFilterDefinition[]): boolean =>
  filterDefinitions.reduce((hasAtLeastOneActiveFilter, atomDefinition) => {
    // Calculate the filter params for this atom definition. Because these are the filters that we're counting to
    // show the user how many are 'active', we want to use the visible atom value if there is one, as that's what the
    // user sees as 'active'.
    const filterParamsFn = atomDefinition.visibleFilterParams || atomDefinition.apiFilterParams;
    const filterParams = filterParamsFn(get);
    if (isEmpty(filterParams)) return hasAtLeastOneActiveFilter;
    return true;
  }, false);

const convertRangeSelectionToFilterParamObject = (filterKey: string, queryValue: number | string | undefined) =>
  isNullish(queryValue) ? {} : toObject(filterKey, queryValue);

/**
 * Convert a list of filter atom definitions into a filter params object. The generated filter params object has a
 * structure like this example:
 *
 * <pre>
 * {gte: {"output.aggregates.minutes_played": 300 },
 *  in: {"input.competition_id": [4],
 *       "input.season_id": [318, 317]}}
 * </pre>
 */
export const createFilterParams = (get: Getter, filterDefinitions: FilterDefinition[]): FilterParams => {
  const filterParams = flatten(filterDefinitions).reduce((acc, filterDefinition) => {
    // Get the filter operator (gte, lte, etc.) of this filter definition
    const { filterOperator } = filterDefinition;
    // Get the existing filter params for this filter operator - we will spread this into the result later
    const existingFilterParams = acc[filterOperator];
    // Calculate the additional filter params for this filter definition. Use the API filter params as we're building
    // the filters that we're going to send to the API here
    const additionalFilterParams = filterDefinition.apiFilterParams(get);
    return { ...acc, [filterOperator]: { ...existingFilterParams, ...additionalFilterParams } };
  }, emptyFilterParams);

  return omitBy(filterParams, value => isEmpty(value));
};

/** Return a count of the number of visible filters that are 'active' in the given filter definitions. */
export const countNumVisibleActiveFilters = (get: Getter, filterDefinitions: FilterDefinition[]) =>
  filterDefinitions.reduce((totalActiveFilters, oneOrMoreFilterDefinitions) => {
    // Make sure we're dealing with an array of filter definitions
    const filterDefinitionsArray = Array.isArray(oneOrMoreFilterDefinitions)
      ? oneOrMoreFilterDefinitions
      : [oneOrMoreFilterDefinitions];

    // Filter to just the visible filter definitions as that's what the user sees
    const visibleFilterDefinitions = filterDefinitionsArray.filter(isVisibleFilter);

    return totalActiveFilters + (hasAtLeastOneActiveFilterInGroup(get, visibleFilterDefinitions) ? 1 : 0);
  }, 0);

/**
 * Create a derived atom that contains the current filter objects needed to query the API for the min and max atoms
 * provided. The filterKey is the key in the filter object that will be sent to the API. For example:
 *
 * <pre>
 * Input:
 * filterKey: 'player.age'
 * minAtom value: 25
 * maxAtom value: 30
 *
 * Output atom format:
 * {min: { 'player.age': 25 }, max: { 'player.age': 30 }}
 * </pre>
 *
 * The output atom is suitable for use with the {@link propertyFilterParamsFn} function.
 */
export const minMaxFilterAtom = (
  filterKey: string,
  minAtom: Atom<string | number | undefined>,
  maxAtom: Atom<string | number | undefined>,
): Atom<Record<'min' | 'max', FilterParamsObject>> =>
  atom(get => {
    const min = convertRangeSelectionToFilterParamObject(filterKey, get(minAtom));
    const max = convertRangeSelectionToFilterParamObject(filterKey, get(maxAtom));
    return { min, max };
  });
